import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

export interface ApproveOptionsDialogData {
  title?: string;
  typeLabel?: string;
}

export interface ApproveOptionsDialogResult {
  mode: "now" | "schedule";
  /** ISO string in UTC; set when mode is "schedule". */
  scheduledApprovalUtc?: string;
}

@Component({
  selector: "app-approve-options-dialog",
  templateUrl: "./approve-options-dialog.component.html",
  styleUrls: ["./approve-options-dialog.component.scss"],
})
export class ApproveOptionsDialogComponent {
  mode: "now" | "schedule" = "now";
  /** yyyy-MM-dd from the native date input (local date). */
  scheduleDate = ApproveOptionsDialogComponent.toDateInput(
    new Date(Date.now() + 24 * 60 * 60 * 1000)
  );
  scheduleTime = "09:00";
  readonly minDate = ApproveOptionsDialogComponent.toDateInput(new Date());

  constructor(
    public dialogRef: MatDialogRef<ApproveOptionsDialogComponent, ApproveOptionsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ApproveOptionsDialogData
  ) {}

  private static toDateInput(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  get scheduledAt(): Date | null {
    if (!this.scheduleDate || !this.scheduleTime) return null;
    const [year, month, day] = this.scheduleDate.split("-").map((p) => Number(p));
    const [hours, minutes] = this.scheduleTime.split(":").map((p) => Number(p));
    if ([year, month, day, hours, minutes].some((n) => Number.isNaN(n))) return null;
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }

  get scheduleError(): string | null {
    if (this.mode !== "schedule") return null;
    const at = this.scheduledAt;
    if (!at) return "Pick a date and time.";
    if (at.getTime() <= Date.now()) return "The scheduled time must be in the future.";
    return null;
  }

  get canConfirm(): boolean {
    return this.mode === "now" || !this.scheduleError;
  }

  get timeZoneLabel(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
    } catch {
      return "local time";
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (!this.canConfirm) return;
    if (this.mode === "now") {
      this.dialogRef.close({ mode: "now" });
      return;
    }
    this.dialogRef.close({
      mode: "schedule",
      scheduledApprovalUtc: this.scheduledAt!.toISOString(),
    });
  }
}
